import { FileText, Pencil, Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import { api } from "@/api";
import { formatError } from "@/auth";
import { CommentThread } from "@/components/projects/CommentThread";
import { timeAgo } from "@/components/projects/common";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

function Linkified({ text }) {
  const parts = text.split(/(https?:\/\/[^\s)]+)/g);
  return <div className="pj-doc-read" data-testid="doc-read-body">{parts.map((p, i) => (i % 2 ? <a key={i} href={p} target="_blank" rel="noopener noreferrer">{p.length > 70 ? `${p.slice(0, 70)}…` : p}</a> : p))}</div>;
}

function DocEditor({ project, doc, members, onClose, onSaved }) {
  const [form, setForm] = useState({ title: doc?.title || "", body: doc?.body || "" });
  const [editing, setEditing] = useState(!doc);
  const save = async () => {
    try {
      const { data } = doc ? await api.put(`/projects/${project.id}/docs/${doc.id}`, form) : await api.post(`/projects/${project.id}/docs`, form);
      toast.success("Document enregistré."); setEditing(false); onSaved(data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const remove = async () => { if (window.confirm("Supprimer ce document ?")) { await api.delete(`/projects/${project.id}/docs/${doc.id}`); onSaved(null); onClose(); } };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="pj-dialog pj-dialog-wide" data-testid="doc-editor">
        <DialogTitle className="sr-only">Document</DialogTitle>
        <p className="pj-crumb">{project.name} · Documents</p>
        {editing ? (
          <>
            <textarea className="pj-title-input" rows={1} placeholder="Titre du document" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="doc-title" />
            <textarea className="pj-doc-body" placeholder="Contenu du document…" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="doc-body" />
          </>
        ) : (
          <>
            <h2 className="pj-doc-title" data-testid="doc-read-title">{form.title}</h2>
            <small className="pj-sub">Modifié {timeAgo(doc.updated_at)} par {doc.updated_by}</small>
            {form.body ? <Linkified text={form.body} /> : <p className="pj-empty">Ce document est vide.</p>}
          </>
        )}
        <div className="pj-row-actions">
          {editing ? <button type="button" className="primary-button" onClick={save} disabled={!form.title.trim()} data-testid="doc-save">Enregistrer</button> : <button type="button" className="primary-button" onClick={() => setEditing(true)} data-testid="doc-edit"><Pencil size={14} />Modifier</button>}
          {editing && doc && <button type="button" className="secondary-button" onClick={() => { setForm({ title: doc.title, body: doc.body }); setEditing(false); }} data-testid="doc-cancel">Annuler</button>}
          {doc && <button type="button" className="secondary-button pj-danger" onClick={remove} data-testid="doc-delete"><Trash2 size={14} />Supprimer</button>}
        </div>
        {doc && <CommentThread projectId={project.id} targetType="doc" targetId={doc.id} members={members} />}
      </DialogContent>
    </Dialog>
  );
}

export function DocsTab({ project, members, openId }) {
  const [items, setItems] = useState([]);
  const [current, setCurrent] = useState(undefined);
  const load = useCallback(() => api.get(`/projects/${project.id}/docs`).then(({ data }) => { setItems(data); return data; }).catch(() => []), [project.id]);
  useEffect(() => { load().then((d) => { const x = d.find((i) => i.id === openId); if (x) setCurrent(x); }); }, [load, openId]);
  return (
    <div data-testid="docs-tab">
      <div className="pj-toolbar"><button type="button" className="primary-button" onClick={() => setCurrent(null)} data-testid="doc-new-btn"><Plus size={15} />Nouveau document</button></div>
      {items.length === 0 && <p className="pj-empty">Aucun document pour l'instant.</p>}
      <div className="pj-docs">
        {items.map((d) => (
          <button type="button" key={d.id} className="pj-doc" onClick={() => setCurrent(d)} data-testid={`doc-${d.id}`}>
            <FileText size={22} /><strong>{d.title}</strong><p>{d.body.slice(0, 140) || "Document vide"}</p><small>{d.updated_by} · {timeAgo(d.updated_at)}</small>
          </button>
        ))}
      </div>
      {current !== undefined && <DocEditor key={current?.id || "new"} project={project} doc={current} members={members} onClose={() => setCurrent(undefined)} onSaved={(d) => { load(); if (d) setCurrent(d); }} />}
    </div>
  );
}
